/* ============================================================
   refresh.js — single-flight token refresh.

   The refresh token is an HttpOnly cookie (Path=/users) that JS never sees:
   POST /users/refresh reads it, returns a new access token, and re-sets the
   cookie for another JWT_REFRESH_TOKEN_EXPIRE_DAYS (2). That re-set is the
   inactivity window — idle for 2 days and the browser has dropped the cookie,
   the refresh 401s, and the user is sent to /auth/login?expired=1.

   WHY SINGLE-FLIGHT STILL MATTERS
   -------------------------------
   Every refresh mints a new pair and there is no jti tracking server-side, so
   concurrent refreshes all succeed with different access tokens. Whichever
   setSession() lands second wins and requests in flight with the loser's token
   see intermittent 401s. So: one promise per tab, and a Web Locks mutex across
   tabs (the cookie is shared by every tab).
   ============================================================ */

import { ApiError, SessionExpiredError } from './errors'
import { getSession, getAccessToken, setSession, clearSession, reloadFromStorage } from './tokenStore'

const API_BASE = import.meta.env.VITE_API_BASE ?? ''

/* WHICH FAILURES END THE SESSION
   ------------------------------
   Only a definitive answer from the API about the refresh token itself: 401
   (cookie missing/expired/invalid) or 403 (user deactivated). Everything that
   says "the server could not answer right now" — a dropped connection, 408,
   425, 429, any 5xx (nginx 502 while the API restarts, 503/504 under load) —
   is transient: the cookie is still good, so the session is kept and the
   refresh is retried. Clearing on those used to sign every tab out whenever
   the backend blinked. */
const TRANSIENT_STATUS = new Set([408, 425, 429])
const isTransient = (status) => TRANSIENT_STATUS.has(status) || status >= 500

/* Retry policy for transient failures: exponential backoff with FULL jitter,
   so thousands of tabs that failed in the same second do not all come back in
   the same second. Retry-After is honoured, capped. Mutable only so the token
   test can shrink the delays. */
export const refreshRetryPolicy = { attempts: 3, baseMs: 1000, maxMs: 10_000 }

function backoffMs(attempt, retryAfterHeader) {
  const retryAfterS = Number(retryAfterHeader)
  if (Number.isFinite(retryAfterS) && retryAfterS > 0) {
    return Math.min(retryAfterS * 1000, refreshRetryPolicy.maxMs)
  }
  const ceiling = Math.min(refreshRetryPolicy.baseMs * 2 ** (attempt - 1), refreshRetryPolicy.maxMs)
  return Math.random() * ceiling
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

let inFlight = null

/**
 * Refresh the session. Concurrent callers all await the SAME promise, so at most
 * one POST /users/refresh is outstanding per tab at any moment.
 */
export function refreshSession() {
  if (inFlight) return inFlight
  inFlight = run().finally(() => {
    inFlight = null
  })
  return inFlight
}

async function post() {
  let status = 0
  for (let attempt = 1; attempt <= refreshRetryPolicy.attempts; attempt += 1) {
    let res = null
    try {
      res = await fetch(`${API_BASE}/users/refresh`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        // The refresh token rides the HttpOnly cookie — no body, no header.
        // `include` so the cookie is also sent when VITE_API_BASE is another origin.
        credentials: 'include',
      })
    } catch {
      res = null // network failure: transient, retry below
    }

    if (res?.ok) {
      // setSession preserves `permissions` from /users/me.
      return setSession(await res.json())
    }
    status = res?.status ?? 0
    if (res && !isTransient(status)) {
      // 401/403 (or any other definitive rejection): the session is over.
      clearSession()
      throw new SessionExpiredError()
    }
    if (attempt < refreshRetryPolicy.attempts) {
      await sleep(backoffMs(attempt, res?.headers?.get?.('Retry-After')))
    }
  }

  // Still unreachable after every attempt. The cookie may well be valid, so the
  // session stays; the caller reports a failed request and the next request
  // tries the refresh again.
  throw new ApiError('Unable to reach the server while renewing your session.', status, null)
}

async function run() {
  reloadFromStorage()
  if (!getSession()) throw new SessionExpiredError()
  const before = getAccessToken()

  // Cross-tab single flight. Without it, five open tabs hitting an expired token
  // means five refreshes and four tabs holding a superseded access token.
  if (typeof navigator !== 'undefined' && navigator.locks) {
    return navigator.locks.request('tf-auth-refresh', async () => {
      reloadFromStorage()
      // Another tab refreshed while we waited for the lock — reuse its result.
      const current = getAccessToken()
      if (current && current !== before) return getSession()
      return post()
    })
  }
  return post()
}

/** Clear the HttpOnly refresh cookie server-side. Best effort: sign-out never waits on it. */
export async function logoutSession() {
  try {
    await fetch(`${API_BASE}/users/logout`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      credentials: 'include',
    })
  } catch {
    /* offline — the local session is cleared regardless */
  }
}
